import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import type { Novel } from '../../services/types';
import { CoverImage } from '../CoverImage';

interface AddNovelToSeriesModalProps {
  seriesId: string;
  existingNovelIds: string[];
  isOpen: boolean;
  onClose: () => void;
  onAdd: (novelId: string) => Promise<void>;
}

export function AddNovelToSeriesModal({
  existingNovelIds,
  isOpen,
  onClose,
  onAdd
}: AddNovelToSeriesModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Novel[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 防抖搜尋
  useEffect(() => {
    if (!isOpen) return;

    if (!query.trim()) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearching(true);
      setError(null);
      try {
        const data = await api.searchNovels(query, 20);
        // 排除已在系列中的小說
        setResults(data.filter(n => !existingNovelIds.includes(n.id)));
      } catch (err) {
        setError('搜尋失敗');
        console.error(err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query, existingNovelIds, isOpen]);

  // 重置狀態
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setResults([]);
      setSelectedId(null);
      setError(null);
    }
  }, [isOpen]);

  const handleAdd = async () => {
    if (!selectedId) return;

    setIsAdding(true);
    setError(null);

    try {
      await onAdd(selectedId);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : '加入失敗');
    } finally {
      setIsAdding(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal">
      {/* Backdrop */}
      <div className="overlay" onClick={onClose} />

      {/* Modal */}
      <div className="modal-content max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="card-header flex items-center justify-between">
          <h3 className="heading-section">
            加入小說到系列
          </h3>
          <button
            onClick={onClose}
            className="btn btn-ghost btn-icon btn-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-neutral-100 dark:border-neutral-700">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜尋小說名稱或作者..."
              className="input pl-10"
              autoFocus
            />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-4 p-3 bg-danger-50 dark:bg-danger-900/20 text-danger-600 dark:text-danger-400 rounded-button text-sm">
            {error}
          </div>
        )}

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-2 scrollbar-thin">
          {isSearching ? (
            <div className="flex items-center justify-center py-8">
              <div className="relative">
                <div className="w-8 h-8 border-2 border-neutral-200 dark:border-neutral-700 rounded-full" />
                <div className="absolute inset-0 w-8 h-8 border-2 border-transparent border-t-primary-500 rounded-full animate-spin" />
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="text-center py-8 body-base">
              {query.trim() ? '找不到符合的小說' : '輸入關鍵字搜尋小說'}
            </div>
          ) : (
            <div className="space-y-1">
              {results.map((novel) => (
                <button
                  key={novel.id}
                  onClick={() => setSelectedId(novel.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-button text-left transition-colors ${
                    selectedId === novel.id
                      ? 'bg-primary-50 dark:bg-primary-900/30 ring-2 ring-primary-500'
                      : 'hover:bg-neutral-50 dark:hover:bg-neutral-700'
                  }`}
                >
                  {/* 封面縮圖 */}
                  <div className="w-12 h-16 flex-shrink-0 bg-neutral-100 dark:bg-neutral-600 rounded overflow-hidden">
                    <CoverImage
                      src={novel.cover_url}
                      alt={novel.title}
                      className="w-full h-full object-cover"
                      fallback={
                        <div className="w-full h-full flex items-center justify-center text-neutral-400 text-lg font-bold">
                          {novel.title.charAt(0)}
                        </div>
                      }
                    />
                  </div>

                  {/* 資訊 */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-neutral-900 dark:text-neutral-50 truncate">
                      {novel.title}
                    </div>
                    <div className="text-sm text-neutral-500 dark:text-neutral-400">
                      {novel.author || '未知作者'} · {novel.crawled_chapters} 章
                    </div>
                  </div>

                  {/* 選中標記 */}
                  {selectedId === novel.id && (
                    <svg
                      className="w-5 h-5 text-primary-600 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="card-footer flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="btn btn-ghost btn-md"
          >
            取消
          </button>
          <button
            onClick={handleAdd}
            disabled={!selectedId || isAdding}
            className="btn btn-primary btn-md"
          >
            {isAdding ? '加入中...' : '加入系列'}
          </button>
        </div>
      </div>
    </div>
  );
}
